import React, { MouseEvent, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Cell, Column, TableProps as ReactTableProps, Row, useRowSelect, useTable } from 'react-table';
import styled, { css } from 'styled-components';

import { Button, Dropdown, Icon, Tooltip } from '@column/column-ui-kit';

import { LogoLoading } from '~/elements';
import { useScrollStore } from '~/stores/Scroll';
import { Flex, Inner } from '~/styles';

import { PopoverFilter, PopoverFilterEntry, PopoverList } from './Popover';
import { SearchBar } from './SearchBar';

export type { Cell };

type TableVariant = 'default' | 'bordered';

export interface TableStyleProps {
  scrollable: boolean;
  isLoading: boolean;
  variant: TableVariant;
}

export type TableColumn<T extends object = Record<string, any>> = Column<T> & {
  width?: string;
  timeFormat?: {
    selectedOption: string;
    onOptionChange: (value: string) => void;
  };
};

export type TableData<T extends object = Record<string, any>> = T & {
  type?: string;
};

export interface TableProps<T extends TableData = TableData> extends Partial<TableStyleProps> {
  columns: TableColumn<T>[];
  data: T[];
  filterElement?: ReactNode;
  filter?: PopoverFilterEntry[];
  onFilterChange?: (filter: PopoverFilterEntry[]) => void;
  onSearchSubmit?: (value: string) => void;
  searchTooltip?: ReactNode;
  searchShowReset?: boolean;
  searchLoading?: boolean;
  showColumnsFilter?: boolean;
  className?: string;
  maxHeight?: string;
  action?: ReactNode;
  hasNextPage?: boolean;
  onRowClick?: (row: Row<T>) => void;
}

const TablePlaceholder = styled.div`
  transition: height 0.2s;
  min-width: 0;
`;

const TableWrapper = styled.div<TableStyleProps>`
  position: relative;
  box-sizing: border-box;
  width: 100%;
`;

const Toolbar = styled.div<{ $isScrolled: boolean }>`
  display: flex;
  gap: 16px;
  padding: 0 var(--page-margin) 12px;
  justify-content: space-between;
  background: ${({ theme }) => theme.background};
  border-bottom: 1px solid;
  border-bottom-color: transparent;
  position: sticky;
  top: 0;
  z-index: 11;
  margin-top: -1px;
  transition: border-bottom-color 0.2s;

  ${({ $isScrolled }) =>
    $isScrolled &&
    css`
      border-bottom-color: ${({ theme }) => theme.secondary.blendToBackground(150)};
    `}
`;

const StyledPopoverFilter = styled(PopoverFilter)`
  --popover-top: 36px;
  --popover-left: 24px;
  z-index: 10;
`;

const ShowColumnFilter = styled.div`
  position: relative;
`;

const StyledPopoverList = styled(PopoverList)`
  --popover-left: auto;
  --popover-right: 0;
  --popover-top: 36px;
`;

const Count = styled.div`
  width: 16px;
  height: 16px;
  background-color: ${({ theme }) => theme.primary.background};
  color: ${({ theme }) => theme.primary.foreground};
  font-size: 9px;
  font-weight: bold;
  line-height: 16px;
  text-align: center;
  border-radius: 3px;
`;

export const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  position: relative;
  container-type: inline-size;
  container-name: table-actions;
`;

const ScrollableTableEllipsis = styled.div<{ $showLeftShadow: boolean; $showRightShadow: boolean }>`
  position: relative;
  width: 100%;
  max-width: 100%;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.secondary.blendToBackground(150)};
  overflow: hidden;

  &:before,
  &:after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    width: 8px;
    pointer-events: none;
    transition: opacity 0.2s;
    z-index: 1;
  }

  &:before {
    left: 0;
    background: linear-gradient(to right, rgba(0, 0, 0, 0.025) 10%, rgba(0, 0, 0, 0));
    opacity: ${({ $showLeftShadow }) => ($showLeftShadow ? 1 : 0)};
  }

  &:after {
    right: 0;
    background: linear-gradient(to left, rgba(0, 0, 0, 0.025) 10%, rgba(0, 0, 0, 0));
    opacity: ${({ $showRightShadow }) => ($showRightShadow ? 1 : 0)};
  }
`;

const ScrollableTableContainer = styled.div`
  width: 100%;
  max-width: 100%;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  min-width: 0;
  display: block;
  contain: inline-size;
`;

const StyledTable = styled.table<TableStyleProps & ReactTableProps & { columnsTemplate?: string }>`
  display: grid;
  will-change: height;
  transition: height 0.2s;
  column-gap: 16px;
  min-width: 100%;
  width: max-content;
  box-sizing: border-box;

  ${({ columnsTemplate }) =>
    columnsTemplate &&
    css`
      grid-template-columns: ${columnsTemplate};
    `};
`;

const TableHead = styled.thead<TableStyleProps>`
  display: grid;
  grid-template-columns: subgrid;
  grid-column: 1/-1;
  align-items: center;
`;

const TableHeadRow = styled.tr<TableStyleProps>`
  display: grid;
  grid-template-columns: subgrid;
  grid-column: 1 / -1;
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.secondary.blendToBackground(150)};
`;

const TableHeader = styled.td`
  padding: 8px 0;
  font-size: 14px;
  font-weight: 600;
  display: block;
  text-align: left;
  line-height: 16px;
  color: ${({ theme }) => theme.secondary.blendToBackground(600)};
`;

const TableHeadCell = styled.th<TableStyleProps & { showToolbar: boolean; isAction: boolean }>`
  padding: 12px 0;
  text-align: left;
  font-size: 14px;
  font-weight: 500;
  line-height: 16px;
  white-space: nowrap;

  color: ${({ theme }) => theme.secondary.blendToBackground(700)};

  &:first-child {
    padding-left: 16px;
  }

  &:last-child {
    padding-right: 16px;
  }

  ${({ isAction }) =>
    isAction &&
    css`
      & > div > svg {
        display: none;
      }
    `}
`;

const StyledBody = styled.tbody<TableStyleProps & Pick<TableProps, 'maxHeight'>>`
  display: grid;
  grid-template-columns: subgrid;
  grid-column: 1 / -1;
  transition: opacity 0.3s;

  ${({ isLoading }) =>
    isLoading &&
    css`
      opacity: 0;
      transition-duration: 0.1s;
    `}

  ${({ scrollable }) =>
    scrollable &&
    css<TableStyleProps & Pick<TableProps, 'maxHeight'>>`
      max-height: ${({ maxHeight }) => maxHeight};
      overflow-y: scroll;
      -ms-overflow-style: none;
      scrollbar-width: none;
      flex-grow: 1;

      &::-webkit-scrollbar {
        display: none;
      }
    `}
`;

const EmptyRow = styled.td`
  display: grid;
  grid-template-columns: subgrid;
  grid-column: 1 / -1;
  padding: 10px 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 24px;
  color: ${({ theme }) => theme.secondary.blendToBackground(600)};
`;

const TableRow = styled.tr<TableStyleProps & { isClickable?: boolean }>`
  display: grid;
  grid-template-columns: subgrid;
  grid-column: 1 / -1;
  align-items: center;
  border-bottom: 1px solid ${({ theme }) => theme.secondary.blendToBackground(150)};
  position: relative;
  background-color: transparent;
  transition: background-color 0.2s;

  &:last-child {
    border-bottom: none;
  }

  td {
    position: relative;
    z-index: 1;
  }

  ${({ isClickable }) =>
    isClickable &&
    css`
      &:hover {
        background-color: ${({ theme }) => theme.secondary.blendToBackground(50)};
      }
    `}
`;

const TableCell = styled.td<
  TableStyleProps & {
    isTitle: boolean;
    isAction: boolean;
    isClickable: boolean;
  }
>`
  padding: 12px 0;
  font-size: 14px;
  line-height: 16px;
  display: block;
  color: ${({ theme }) => theme.secondary.background};
  transition: background-color 0.1s;
  position: relative;

  &:first-child {
    padding-left: 16px;
  }

  &:last-child {
    padding-right: 12px;
  }

  ${({ isTitle }) =>
    isTitle &&
    css`
      font-size: 14px;
      font-weight: 500;
      color: ${({ theme }) => theme.foreground};
    `}

  ${({ isAction }) =>
    isAction &&
    css`
      & > div {
        display: flex;
      }
    `}

  ${({ isClickable }) =>
    isClickable &&
    css`
      cursor: pointer;
    `}
`;

const FullRow = styled.tr`
  display: grid;
  grid-column: 1 / -1;
  align-items: center;
  text-align: center;
`;

const StyledLogoLoading = styled(LogoLoading)`
  top: calc(50% + 49px);
`;

const TimeFormatDropdown = styled(Dropdown)`
  width: 100%;

  button {
    padding: 0;
    box-shadow: none;

    &:after {
      display: none;
    }
  }
`;

const TimeFormatColumn: React.FC<{
  selectedOption: string;
  onOptionChange: (value: string) => void;
  children: ReactNode;
}> = ({ selectedOption, onOptionChange, children }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  useEffect(() => {
    if (isDropdownOpen) {
      const handleScroll = () => {
        setIsDropdownOpen(false);
      };

      const pageFrame = document.querySelector('#scroll-container main');
      if (pageFrame) {
        pageFrame.addEventListener('scroll', handleScroll, { passive: true });
        return () => pageFrame.removeEventListener('scroll', handleScroll);
      }
    }
  }, [isDropdownOpen]);

  return (
    <TimeFormatDropdown
      options={[
        { label: 'Relative Time', value: 'relative' },
        { label: 'Date / Time', value: 'absolute' },
      ]}
      size="small"
      positionRight
      active={selectedOption}
      isOpen={isDropdownOpen}
      onOpenChange={setIsDropdownOpen}
      onChange={onOptionChange}
      customLabel={
        <Flex alignItems="center" gap={4} justifyContent="space-between">
          {children} {isDropdownOpen ? <Icon.ChevronUp /> : <Icon.ChevronDown />}
        </Flex>
      }
    />
  );
};

export const Table = <T extends TableData = TableData>(props: TableProps<T>) => {
  const tableRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const headRef = useRef<HTMLTableSectionElement>(null);
  const bodyRef = useRef<HTMLTableSectionElement>(null);
  const [customList, setCustomList] = useState<string[] | boolean>(true);
  const [showFilterPopover, setShowFilterPopover] = useState<boolean>(false);
  const [showListPopover, setShowListPopover] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchFocus, setSearchFocus] = useState<boolean>(false);
  const [prev, setPrev] = useState<number | null>(null);
  const pageFrameScroll = useScrollStore((s) => s.pageFrameScroll);
  const isScrolled = (pageFrameScroll ?? 0) > 0;
  const [showLeftShadow, setShowLeftShadow] = useState<boolean>(false);
  const [showRightShadow, setShowRightShadow] = useState<boolean>(false);

  const styleProps: TableStyleProps = {
    scrollable: props.scrollable ?? false,
    isLoading: loading,
    variant: props.variant ?? 'default',
  };

  const data = useMemo(() => props.data, [props.data]);

  const columns = useMemo(
    () =>
      props.columns.map((column: TableColumn<T>) => {
        if (typeof column.accessor === 'string') {
          column.id = column.accessor;
        }
        return column;
      }),
    [props.columns]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    allColumns,
    setHiddenColumns,
    toggleHideAllColumns,
  } = useTable<T>(
    {
      columns,
      data,
    },
    useRowSelect
  );

  const handleRowClick = (e: MouseEvent<HTMLTableRowElement>, row: Row<T>) => {
    e.stopPropagation();

    if (props.onRowClick) {
      props.onRowClick(row);
    }
  };

  useEffect(() => {
    if (typeof customList === 'boolean') {
      toggleHideAllColumns(!customList);
    } else {
      setHiddenColumns(
        allColumns
          .filter((e: any) => !customList.includes(e.id))
          .map((e: any) => {
            if (e.id !== 'action') {
              return e.id;
            }
          })
      );
    }
  }, [customList]);

  useEffect(() => {
    if (!tableRef.current || !headRef.current || !bodyRef.current) {
      return;
    }

    const table = tableRef.current;
    const head = headRef.current;
    const body = bodyRef.current;

    if (props.isLoading) {
      setPrev(table.offsetHeight);
      table.style.height = `${prev || table.offsetHeight}px`;
      setLoading(true);
    } else {
      if (loading) {
        if (prev && prev !== table.offsetHeight) {
          table.style.height = `${prev}px`;
        }

        setTimeout(() => {
          table.style.height = `${head.offsetHeight + body.offsetHeight}px`;

          setTimeout(() => {
            setLoading(false);
          }, 150);
        }, 0);
      } else {
        table.style.height = `${head.offsetHeight + body.offsetHeight}px`;
      }
    }
  }, [props.isLoading, props.data, loading, prev]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) {
      return;
    }

    const updateShadows = () => {
      const hasOverflow = el.scrollWidth > el.clientWidth + 1;
      if (!hasOverflow) {
        setShowLeftShadow(false);
        setShowRightShadow(false);
        return;
      }

      const atStart = el.scrollLeft <= 0;
      const atEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 1;

      setShowLeftShadow(!atStart);
      setShowRightShadow(!atEnd);
    };

    updateShadows();
    el.addEventListener('scroll', updateShadows, { passive: true });
    window.addEventListener('resize', updateShadows);

    const ro = new ResizeObserver(updateShadows);
    ro.observe(el);

    return () => {
      el.removeEventListener('scroll', updateShadows as EventListener);
      window.removeEventListener('resize', updateShadows);
      ro.disconnect();
    };
  }, [scrollContainerRef.current]);

  const showToolbar =
    (props.filter && props.filter.length > 0) || props.showColumnsFilter || !!props.action || props.filterElement;

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
  };

  const handleSearchSubmit = (value: string) => {
    if (props.onSearchSubmit) {
      props.onSearchSubmit(value);
    }
  };

  const getColumnsTemplate = (): string => {
    const columnsTemplate = props.columns
      .map((column: TableColumn<T>) => {
        if (column.width) {
          return `${column.width}`;
        }
        return 'minmax(100px, max-content)';
      })
      .join(' ');
    return columnsTemplate;
  };

  return (
    <TableWrapper className={props.className} {...styleProps}>
      {showToolbar && (
        <Toolbar $isScrolled={isScrolled}>
          {props.filterElement}
          {props.filter && props.filter.length > 0 && (
            <Button variant="secondary" size="small" icon={<Icon.Filter />} onClick={() => setShowFilterPopover(true)}>
              Add Filter
              {props.filter.filter((e: PopoverFilterEntry) => e.show === true).length > 0 && (
                <Count>{props.filter.filter((e: PopoverFilterEntry) => e.show === true).length}</Count>
              )}
            </Button>
          )}
          {props.filter && (
            <StyledPopoverFilter
              show={props.filter && props.filter.length > 0 && showFilterPopover}
              options={props.filter}
              onOptionChange={(filter: PopoverFilterEntry[]) => {
                if (props.onFilterChange) {
                  props.onFilterChange(filter);
                }
              }}
              onClose={() => setShowFilterPopover(false)}
              onDone={() => setShowFilterPopover(false)}
              onReset={() => {
                if (props.filter && props.onFilterChange) {
                  props.onFilterChange(
                    props.filter.map((e: PopoverFilterEntry) => {
                      e.show = false;
                      return e;
                    })
                  );
                }
              }}
            />
          )}
          {props.onSearchSubmit &&
            (props.searchTooltip ? (
              <Tooltip
                offsetX={-22}
                offsetArrowX={-92}
                placement="top-start"
                content={props.searchTooltip}
                isOpen={searchFocus && searchTerm.length < 1}
              >
                <SearchBar
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onSearchSubmit={handleSearchSubmit}
                  onFocusChange={(value: boolean) => setSearchFocus(value)}
                  searchShowReset={props.searchShowReset}
                  placeholder="Search"
                  isLoading={props.searchLoading}
                />
              </Tooltip>
            ) : (
              <SearchBar
                value={searchTerm}
                onChange={handleSearchChange}
                onSearchSubmit={handleSearchSubmit}
                searchShowReset={props.searchShowReset}
                placeholder="Search"
                isLoading={props.searchLoading}
              />
            ))}
          <Actions>
            {props.showColumnsFilter && (
              <ShowColumnFilter>
                <Button
                  variant="subtle"
                  size="small"
                  icon={<Icon.TableEdit />}
                  onClick={() => setShowListPopover(true)}
                >
                  Settings
                </Button>
                <StyledPopoverList
                  show={showListPopover}
                  options={allColumns
                    .filter((e: any) => e.Header.length > 0 && e.id && e.id !== 'selection')
                    .map((entry: any) => ({
                      label: entry.Header,
                      id: entry.id,
                    }))}
                  active={customList}
                  onActiveChange={(options: string[] | boolean) => setCustomList(options)}
                  onClose={() => setShowListPopover(false)}
                  onDone={() => setShowListPopover(false)}
                  onReset={() => setCustomList(true)}
                />
              </ShowColumnFilter>
            )}
            {props.action}
          </Actions>
        </Toolbar>
      )}

      <Inner py={0} px="var(--page-margin)">
        <TablePlaceholder ref={tableRef}>
          <ScrollableTableEllipsis $showLeftShadow={showLeftShadow} $showRightShadow={showRightShadow}>
            <ScrollableTableContainer ref={scrollContainerRef}>
              <StyledTable {...getTableProps()} columnsTemplate={getColumnsTemplate()} {...styleProps}>
                <TableHead ref={headRef} {...styleProps}>
                  {headerGroups.map((headerGroup) => {
                    const { key: headerGroupKey, ...headerGroupProps } = headerGroup.getHeaderGroupProps();
                    return (
                      <TableHeadRow key={headerGroupKey} {...headerGroupProps} {...styleProps}>
                        {(headerGroup.headers as any).map((column: any, index: number) => {
                          const { key: headerKey, ...headerProps } = column.getHeaderProps();
                          const columnConfig = column as TableColumn<T>;

                          return (
                            <TableHeadCell
                              key={headerKey}
                              showToolbar={showToolbar}
                              isAction={index === headerGroup.headers.length - 1}
                              {...headerProps}
                              {...styleProps}
                            >
                              {columnConfig.timeFormat ? (
                                <TimeFormatColumn
                                  selectedOption={columnConfig.timeFormat.selectedOption}
                                  onOptionChange={columnConfig.timeFormat.onOptionChange}
                                >
                                  {column.render('Header')}
                                </TimeFormatColumn>
                              ) : (
                                column.render('Header')
                              )}
                            </TableHeadCell>
                          );
                        })}
                      </TableHeadRow>
                    );
                  })}
                </TableHead>
                <StyledBody {...styleProps} maxHeight={props.maxHeight} {...getTableBodyProps()} ref={bodyRef}>
                  {rows.map((row) => {
                    prepareRow(row);
                    const original = row.original;
                    const { key: rowKey, ...rowProps } = row.getRowProps();
                    if (original?.type === 'header') {
                      return (
                        <FullRow key={rowKey} {...rowProps}>
                          <TableHeader colSpan={columns.length}>{original.name}</TableHeader>
                        </FullRow>
                      );
                    }
                    return (
                      <TableRow
                        key={rowKey}
                        {...rowProps}
                        isClickable={typeof props.onRowClick !== 'undefined'}
                        onClick={(e: MouseEvent<HTMLTableRowElement>) => handleRowClick(e, row)}
                        {...styleProps}
                      >
                        {row.cells.map((cell, index) => {
                          const { key: cellKey, ...cellProps } = cell.getCellProps();
                          return (
                            <TableCell
                              key={cellKey}
                              isTitle={index === 0}
                              isAction={index === row.cells.length - 1}
                              isClickable={typeof props.onRowClick !== 'undefined'}
                              {...cellProps}
                              {...styleProps}
                            >
                              {cell.render('Cell')}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                  {rows.length < 1 && (
                    <FullRow>
                      <EmptyRow colSpan={columns.length}>No entries found</EmptyRow>
                    </FullRow>
                  )}
                </StyledBody>
              </StyledTable>
            </ScrollableTableContainer>
          </ScrollableTableEllipsis>
        </TablePlaceholder>
      </Inner>

      {loading && <StyledLogoLoading />}
    </TableWrapper>
  );
};
